import React, { useCallback, useEffect, useState } from "react";
import { appLocalStore, appNoteStore } from "../model/services";
import { exportNotes, ExportType } from "../export/ExportModalContent";
import { modelVersion, storageType } from "../utils/environment";
import { RohanCache } from "../service-worker/rohanCache";
import { CacheKeys } from "../service-worker/swCache";
import { ApiClient } from "../api/client";
import { Note, NoteWithPosition, SettledNote } from "../../shared/types";
import { useAuth } from "../auth/useAuth";
import { settleNotes } from "../model/positionGenerator";
import { Badge } from "../components/Badge";

function DebugSection() {
  const { logout, user } = useAuth();
  const [isDebugModeOn, setIsDebugModeOn] = useState(false);
  const [noteCountInServer, setNoteCountInServer] = useState(0);
  const [noteCountInMemory, setNoteCountInMemory] = useState(0);
  const [noteCountInCache, setNoteCountInCache] = useState(0);
  const [dirtyNoteIds, setDirtyNodeIds] = useState<string[]>([]);

  const cache = new RohanCache(
    [
      CacheKeys.notes,
      CacheKeys.folders,
      CacheKeys.notesDirtySet,
      CacheKeys.foldersDirtySet,
      CacheKeys.user,
      CacheKeys.metadata,
    ],
    modelVersion,
  );

  const notesTable = cache.tables.get(CacheKeys.notes);
  const dirtyNotesTable = cache.tables.get(CacheKeys.notesDirtySet);

  const loadCacheStats = useCallback(async () => {
    if (notesTable) {
      setNoteCountInCache(Object.keys((await notesTable.load()).data).length);
    }
    if (dirtyNotesTable) {
      setDirtyNodeIds((await dirtyNotesTable.load()).data.upserts || 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadServerStats = useCallback(async () => {
    const serverStats = await ApiClient().notes.getStats();
    setNoteCountInServer(serverStats.aggregate.count);
  }, []);

  const loadStats = useCallback(() => {
    if (!isDebugModeOn) return;
    loadCacheStats().then(() => {
      console.log(`Loaded stats directly from ${storageType}`);
    });
    loadServerStats().then(() => {
      console.log(`Loaded stats directly from server`);
    });
    setNoteCountInMemory(appNoteStore.getAll().length);
  }, [loadCacheStats, loadServerStats, isDebugModeOn]);

  useEffect(() => {
    loadStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDebugModeOn]);

  if (!notesTable || !dirtyNotesTable) {
    return <span>Unable to connect to {storageType}</span>;
  }

  const hasDirtyNotes = dirtyNoteIds.length;

  const syncBadge = (
    <Badge
      type={hasDirtyNotes ? "danger" : "safe"}
      text={hasDirtyNotes ? `${dirtyNoteIds.length} unsynced` : "All persisted"}
    ></Badge>
  );

  const stopServiceWorker = async (): Promise<void> => {
    const registrations = await navigator.serviceWorker.getRegistrations();
    const unregistrations = registrations.map((registration) => registration.unregister());

    await Promise.all(unregistrations);

    alert(`Unregistered ${registrations.length} service workers`);
  };

  const persistNotes = async () => {
    if (!user) return;
    const dirtyNodeIdLookupSet = new Set(dirtyNoteIds);
    const allNotes: Note[] = Object.values((await notesTable.load()).data);
    const dirtyNotes = allNotes.filter((note) => dirtyNodeIdLookupSet.has(note.id));
    const { alreadySettled, newlySettled } = settleNotes(dirtyNotes, allNotes);
    const notesWithStringPosition = [...newlySettled, ...alreadySettled].map((note) => ({
      ...note,
      position: note.position.position,
    }));

    async function uploadNotesInParallel(notes: NoteWithPosition[], size = 250) {
      const chunks = Array.from({ length: Math.ceil(notes.length / size) }, (_, i) =>
        notes.slice(i * size, i * size + size),
      );

      console.log(chunks);

      await Promise.all(
        chunks.map((chunk, i) =>
          ApiClient()
            .notes.upsertNotes(chunk)
            .then(() => console.log(`Uploaded chunk ${i + 1}/${chunks.length}`))
            .catch((err) => console.error(`Chunk ${i + 1} failed:`, err)),
        ),
      );

      console.log("All chunks uploaded. Hopefully.");
    }

    await uploadNotesInParallel(notesWithStringPosition);
    await notesTable.vacuum(true);
    await dirtyNotesTable.clear();
    alert(`${notesWithStringPosition.length} persisted`);
    loadStats();
  };

  const exportDirtyNotes = async () => {
    if (!user) return;
    const dirtyNodeIdLookupSet = new Set(dirtyNoteIds);
    const allNotes: Note[] = Object.values((await notesTable.load()).data);
    const dirtyNotes = allNotes.filter((note) => dirtyNodeIdLookupSet.has(note.id));
    //Todo: These are not actually Settled albeit.
    exportNotes(dirtyNotes as SettledNote[], user.id, ExportType.JSON);
  };

  const nukeAll = async () => {
    await stopServiceWorker();
    await cache.clear();
    appLocalStore.debugReset();
    logout();
  };

  if (!isDebugModeOn) {
    return (
      <div>
        <button onClick={() => setIsDebugModeOn(true)}>Turn on debug mode.</button>
        <br />
        <span style={{ color: "red", fontSize: "small", fontStyle: "italic" }}>
          (Might crash browser in rare cases)
        </span>
      </div>
    );
  }

  return (
    <div>
      <table>
        <tbody>
          <code>
            This does not uses getSyncStats() for stats because it relies on service worker to talk to {storageType}.
          </code>
          <tr>
            <td>Notes in Memory</td>
            <td>{noteCountInMemory}</td>
          </tr>
          <tr>
            <td>Notes in Storage</td>
            <td>{noteCountInCache}</td>
          </tr>
          <tr>
            <td>Notes in Server</td>
            <td>{noteCountInServer}</td>
          </tr>
          <tr>
            <td>
              Issue description from Service Worker <Badge type={"warning"} text={"Unreliable stat"} />
            </td>
            <td>{"Unknown"}</td>
          </tr>
          <tr>
            <td>Storage Type</td>
            <td>{storageType}</td>
          </tr>
          <tr>
            <td>
              Pending operations count since last full sync <Badge type={"warning"} text={"Unreliable stat"} />
            </td>
            <td>Unknown</td>
          </tr>
        </tbody>
      </table>
      <table>
        <thead>
          <tr>
            <th>Description</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Refresh the stats and metrics on the debug panel.</td>
            <td>
              <button onClick={loadStats}>Refresh</button>
            </td>
          </tr>
          <tr>
            <td>Turns off debug mode and hide this whole panel.</td>
            <td>
              <button onClick={() => setIsDebugModeOn(false)}>Turn off debug mode</button>
            </td>
          </tr>
          <tr>
            <td>Persist notes that haven't been synced to the server. {syncBadge}</td>
            <td>
              <button disabled={!hasDirtyNotes} onClick={persistNotes}>
                Persist {dirtyNoteIds.length || 0} unsynced note
              </button>
            </td>
          </tr>

          <tr>
            <td>Exports notes that haven't been synced to the server. {syncBadge}</td>
            <td>
              {" "}
              <button disabled={!hasDirtyNotes} onClick={exportDirtyNotes}>
                Export {dirtyNoteIds.length || 0} unsynced note
              </button>
            </td>
          </tr>

          <tr>
            <td>
              Terminates the service worker, clears the app cache (client DB) and forces a logout. On the next login,
              all data will need to be re-downloaded.
            </td>

            <td>
              {" "}
              <button onClick={nukeAll}>Nuke All :D</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default DebugSection;
